/**
 * RIEPILOGO AUTOMAZIONE — resa leggibile di un'automazione, in registro telegrafico.
 *
 * Serve in DUE posti: il box "Riepilogo" del builder (mentre si compone) e la card della gallery
 * dei template (su un'automazione persistita). Per questo la funzione prende in ingresso la forma
 * del **DTO** — className, eventType, eventConfig, conditionGroups — e non lo stato dell'editor:
 * il builder le passa il payload che già serializza per la validazione, la gallery le passa
 * l'`Automation` così com'è. Una resa sola, due chiamanti, nessun adattatore.
 *
 * Le etichette NON si costruiscono qui: arrivano dal catalogo, già localizzate dal converter
 * (`AutomationObjectType.label`, `AutomationEvent.label`, `CatalogField.label`,
 * `ConditionOperator.label`, `CatalogEnumValue.label`). Il riepilogo nomina campi e operatori con
 * le stesse parole che l'utente ha letto nelle tendine quando li ha scelti.
 *
 * Le AZIONI fanno eccezione, ed è voluto: si nominano col titolo che l'utente ha scritto, non con
 * l'etichetta del loro tipo. Il titolo nasce pre-riempito da quell'etichetta, ma da lì in poi è
 * testo suo — ed è l'unico modo per distinguere due azioni dello stesso tipo nella stessa
 * automazione.
 *
 * Il registro è TELEGRAFICO, e non è un ripiego: una frase discorsiva in italiano
 * ("una Persona viene creata" / "un Preventivo viene creato") pretende l'accordo di genere, che le
 * etichette del catalogo — sostantivi come "Creato" — non sanno fare. Il telegrafico le riusa così
 * come sono.
 *
 * Ciò che manca compare come SEGNAPOSTO: il riepilogo è sempre una frase intera, e mostra la forma
 * di ciò che non c'è ancora. Resta una guida, non solo uno specchio.
 */

import type { TFunction } from 'i18next';
import type {
  AutomationObjectType,
  AutomationParam,
  AutomationParamType,
  CatalogEnumValue,
  CatalogField,
  ConditionOperator,
} from './automationTypes';
import { DEFAULT_RELATIVE_UNIT } from './automationTypes';

/**
 * Ciò che l'utente non ha ancora scelto. Esportata perché i due chiamanti la mostrano quando un
 * intero blocco è vuoto: un solo segnaposto in tutta l'applicazione, non tre che possono divergere.
 */
export const SUMMARY_PLACEHOLDER = '…';

/** Separatore fra i pezzi di una stessa riga (oggetto · evento · parametri). */
const DOT = ' · ';

/**
 * Catalogo necessario alla resa. Il builder ce l'ha già tutto; la gallery deve caricarselo (una
 * richiesta per gli oggetti, una per ogni className distinto fra i template).
 */
export interface AutomationSummaryCatalog {
  objectTypes: AutomationObjectType[];
  /** Campi filtrabili per FQN dell'entità (la stessa cache per-className dello slice). */
  conditionFieldsByClassName: Record<string, CatalogField[]>;
}

/**
 * L'automazione da raccontare, nella forma minima che serve: la stessa che hanno sia
 * `AutomationCreatePayload` (builder) sia `Automation` (gallery). Tipizzata strutturalmente apposta,
 * così entrambe passano senza conversioni.
 */
export interface SummarizableAutomation {
  className: string;
  eventType: string;
  eventConfig?: string | null;
  /** Riscatto: entra nel QUANDO solo se acceso e solo su un evento temporale. */
  repeatable?: boolean;
  conditionGroups?: SummarizableConditionGroup[];
  actions?: SummarizableAction[];
}

/**
 * L'azione nella forma minima che serve al racconto: il TITOLO, che è come l'utente l'ha chiamata, e
 * il codice, che serve a ricadere sull'etichetta di catalogo quando un titolo non c'è. Ci passano
 * sia `AutomationAction` (gallery) sia `AutomationActionInput` (builder), che li hanno entrambi.
 *
 * Il titolo nasce pre-riempito dall'etichetta del catalogo, ma da quel momento è testo dell'utente —
 * ed è la ragione per cui si persiste invece di derivarlo. Un riepilogo che mostrasse la sola
 * etichetta racconterebbe il TIPO dell'azione invece dell'azione, e due azioni dello stesso tipo
 * nella stessa automazione — che è proprio ciò che il titolo serve a distinguere — sarebbero
 * indistinguibili.
 *
 * Niente `priority`: l'ordine delle azioni è quello dell'array, e non si tocca. Le azioni si
 * eseguono in sequenza, quindi il loro ordine è un CONTENUTO dell'automazione, non una
 * presentazione — il riepilogo lo riporta, non lo decide. Il backend le consegna già ordinate
 * (`<order by="asc"><order-column name="priority"/></order>` su AutomationAction nel service.xml) e
 * l'editor le compone nell'ordine in cui l'utente le mette: `priority` è come quell'ordine viene
 * persistito, non un input della resa. Da non confondere con l'ordinamento del CATALOGO nella
 * tendina di scelta, che è alfabetico e sta al frontend (come oggetti ed eventi del QUANDO).
 */
export interface SummarizableAction {
  code: string;
  title: string;
}

export interface SummarizableConditionGroup {
  conditions: SummarizableCondition[];
}

export interface SummarizableCondition {
  fieldName: string;
  operator: string;
  /** Valore configurato come stringa JSON, o `null` se la riga non è ancora completa. */
  value: string | null;
  valueSource: string;
}

/**
 * Un pezzo del blocco SE. Non una stringa già composta, perché i connettivi logici vanno messi in
 * EVIDENZA a video: sono la struttura della regola, non una parola come le altre, e distinguerli
 * è ciò che permette di leggere "(A e B) O C" a colpo d'occhio. Chi rende decide COME evidenziarli;
 * qui si dice soltanto quali lo sono.
 */
export interface SummaryToken {
  text: string;
  connective?: boolean;
}

/** Le tre righe del riepilogo. */
export interface AutomationSummary {
  when: string;
  if: SummaryToken[];
  then: string[];
}

export function summarizeAutomation(
  automation: SummarizableAutomation,
  catalog: AutomationSummaryCatalog,
  t: TFunction,
): AutomationSummary {
  const objectType = catalog.objectTypes.find((o) => o.className === automation.className);

  // Chiave ASSENTE (catalogo ancora in volo) e lista VUOTA (entità senza campi filtrabili) sono due
  // stati diversi, e appiattirli con un `?? []` fa raccontare al riepilogo la STRUTTURA di condizioni
  // che non sa ancora leggere: nella gallery uscirebbe "(… E …) O …", cioè la logica dettagliata del
  // nulla. Finché il catalogo non è arrivato non si dice niente — è un'attesa, non un contenuto.
  const fields = catalog.conditionFieldsByClassName[automation.className];

  // L'ALLORA non ha un'attesa da gestire, a differenza del SE: le azioni si nominano col proprio
  // titolo, che viaggia sull'automazione, quindi non c'è catalogo da aspettare né chiave da
  // distinguere. È la parte del riepilogo che si completa per prima.
  return {
    when: summarizeWhen(automation, objectType, t),
    if: fields ? summarizeIf(automation, fields, t) : [],
    then: summarizeThen(automation),
  };
}

/**
 * ALLORA: le azioni, nominate col TITOLO che l'utente ha dato loro, NELL'ORDINE IN CUI STANNO.
 * L'array è la sequenza di esecuzione: non si ordina e non si filtra, si riporta.
 *
 * Il titolo prima non arrivava fin qui — `SummarizableAction` portava il solo codice — e il
 * riepilogo mostrava l'etichetta del catalogo, cioè il TIPO dell'azione al posto dell'azione: due
 * "Aggiorna campo" nella stessa automazione si leggevano identici, che è esattamente ciò che il
 * titolo serve a evitare.
 *
 * <b>Senza catalogo</b>, a differenza del QUANDO e del SE: il titolo è obbligatorio lato server e
 * viaggia sull'azione stessa, quindi qui non c'è niente da risolvere e niente da attendere. È la
 * ragione per cui la gallery non chiede più il catalogo azioni: lo caricava solo per questo lookup.
 * Un titolo vuoto — possibile sulle automazioni salvate prima che l'obbligatorietà esistesse —
 * diventa segnaposto invece di sparire: un'azione in meno cambierebbe il racconto in silenzio, e
 * "una cosa che non so nominare" è comunque più vero di "niente".
 */
function summarizeThen(automation: SummarizableAutomation): string[] {
  return (automation.actions ?? []).map((action) => action.title?.trim() || SUMMARY_PLACEHOLDER);
}

/** QUANDO: oggetto · evento (· riscatto) · parametri dell'evento. */
function summarizeWhen(
  automation: SummarizableAutomation,
  objectType: AutomationObjectType | undefined,
  t: TFunction,
): string {
  const event = objectType?.events.find((e) => e.code === automation.eventType);

  const parts = [objectType?.label ?? SUMMARY_PLACEHOLDER, event?.label ?? SUMMARY_PLACEHOLDER];

  // Il riscatto qualifica l'EVENTO, quindi sta subito dopo di lui e prima dei parametri, che lo
  // configurano: è lo stesso ordine dell'editor, e l'informazione si trova nello stesso posto nei
  // due punti in cui l'automazione si legge.
  //
  // Solo quando è ACCESO: il caso normale è "una volta per episodio", e dirlo su ogni riga
  // dell'elenco sarebbe rumore su un'informazione che quasi sempre non interessa. Chi ripete è
  // l'eccezione, ed è quella che va notata.
  //
  // La guardia è DOPPIA — flag acceso e evento davvero temporale — perché il motore ignora il flag
  // sugli eventi real-time: un dato sporco che lo portasse a true farebbe dire al riepilogo una
  // cosa che non succede. `event.scheduled` è già risolto qui sopra, quindi non costa nulla.
  if (automation.repeatable && event?.scheduled)
    parts.push(t('automation.summary.repeats'));

  // I parametri dell'evento (es. "giorni: 3") entrano solo se l'evento ne dichiara: i CRUD non ne
  // hanno, e la riga resta pulita.
  if (event) {
    const config = parseObject(automation.eventConfig);

    for (const param of event.params) {
      const paramValue = parseObject(config?.[param.name]);

      // Un interruttore si legge dal suo nome: acceso dice "Solo giorni lavorativi", spento non
      // aggiunge niente. "Solo giorni lavorativi: true" sarebbe il valore grezzo, non una frase.
      if (param.type === 'BOOLEAN') {
        if (paramValue?.value === true) parts.push(param.label);

        continue;
      }

      const value = formatParamValue(param, paramValue);

      // Un facoltativo non compilato non è un dato mancante: il segnaposto direbbe il contrario, su
      // ogni automazione salvata prima che il parametro esistesse.
      if (value === null && !param.required) continue;

      parts.push(`${param.label}: ${value ?? SUMMARY_PLACEHOLDER}`);
    }
  }

  return parts.join(DOT);
}

/**
 * SE: le condizioni con la loro struttura logica, resa esplicita.
 *
 * Le PARENTESI servono a disambiguare una precedenza, e si mettono solo quando una precedenza c'è
 * davvero: quando i gruppi sono più d'uno (fra loro in O) e il gruppo ne lega più d'una. Senza,
 * "A E B O C" si presta a due letture opposte, e la precedenza degli operatori logici è una
 * convenzione che l'utente del CRM non deve conoscere. Con un gruppo solo — il caso normale, visto
 * che l'editor parte da uno — non c'è nessun O da cui difendersi, e le parentesi sarebbero rumore.
 *
 * Il legame DENTRO un gruppo è SEMPRE AND: la struttura è fissa (DNF, booleanamente completa), non
 * c'è un operatore di gruppo da leggere. L'OR sta solo FRA i gruppi.
 *
 * Il legame FRA gruppi invece è una convenzione (i gruppi sono alternative, quindi OR) e non ha un
 * campo suo: è fissato qui, ed è l'unica cosa che il riepilogo decide da sé.
 */
function summarizeIf(
  automation: SummarizableAutomation,
  fields: CatalogField[],
  t: TFunction,
): SummaryToken[] {
  // Un gruppo senza condizioni non è un'alternativa, è un contenitore vuoto: non deve aggiungere un
  // "O" che non lega nulla, né contare come gruppo quando si decide se servono le parentesi.
  const groups = (automation.conditionGroups ?? []).filter(
    (group) => group.conditions.length > 0,
  );

  const tokens: SummaryToken[] = [];

  groups.forEach((group, groupIndex) => {
    if (groupIndex > 0)
      tokens.push({ text: t('automation.summary.or'), connective: true });

    // Dentro un gruppo le condizioni sono sempre in AND (struttura fissa DNF; l'OR è solo TRA gruppi).
    const withinGroup = t('automation.summary.and');

    const parenthesized = groups.length > 1 && group.conditions.length > 1;

    group.conditions.forEach((condition, index) => {
      if (index > 0) tokens.push({ text: withinGroup, connective: true });

      const open = parenthesized && index === 0 ? '(' : '';
      const close = parenthesized && index === group.conditions.length - 1 ? ')' : '';

      tokens.push({ text: `${open}${summarizeCondition(condition, fields, t)}${close}` });
    });
  });

  return tokens;
}

/**
 * Una condizione spezzata in due: quel che si confronta e il valore con cui lo si confronta.
 *
 * Separati perché chi rende può volerli distinguere — la tabella della simulazione mette il valore
 * in corsivo, per staccare la soglia dalla regola in una colonna fitta — mentre il riepilogo del
 * builder li unisce e basta.
 */
export interface ConditionParts {
  /** Campo e confronto: "Cognome contiene", "Importo ≥". */
  subject: string;
  /** Il valore atteso, già formattato: gli ENUM come etichetta, gli intervalli come "da – a". */
  value: string;
}

/**
 * Una condizione: campo, operatore, valore — con le parole delle tendine da cui l'utente li ha
 * scelti.
 *
 * Esportata perché la usa anche la simulazione, per dire perché un record è escluso: il motivo di
 * un'esclusione È la condizione che non ha fatto match, quindi raccontarla è lo stesso mestiere.
 * Scriverne una seconda versione avrebbe significato duplicare il vocabolario degli operatori, che
 * il backend già dichiara e localizza, e due frasi diverse per la stessa regola nella stessa
 * applicazione.
 *
 * PRETENDE i campi del catalogo condizioni, cioè col proprio `operators`: senza, l'operatore non si
 * risolve e la frase esce con dei segnaposto. Le `columns` della simulazione non bastano — arrivano
 * senza operatori — ed è la ragione per cui quella pagina il catalogo se lo carica.
 */
export function summarizeConditionParts(
  condition: SummarizableCondition,
  fields: CatalogField[],
  t: TFunction,
): ConditionParts | null {
  const field = fields.find((f) => f.fieldName === condition.fieldName);

  // Campo che il catalogo non dichiara — rinominato, o tolto dal provider: la condizione non si sa
  // nominare, ed è UNO stato solo. Tornare due segnaposto lo raddoppierebbe, e chi li unisce si
  // ritroverebbe "… …" al posto di "…". Il ripiego lo sceglie il chiamante, che sa in che spazio
  // sta scrivendo.
  if (!field) return null;

  const operator = field.operators?.find((o) => o.code === condition.operator);

  const value = operator ? formatConditionValue(condition, field, operator, fields, t) : null;

  // Il SIMBOLO dove c'è (=, ≠, ≥ …), la parola dove non c'è (contiene, è uno tra …): il riepilogo
  // deve stare in una colonna stretta, e "Importo ≥ 100" si legge in un colpo d'occhio dove
  // "Importo maggiore o uguale a 100" costringe a rileggere. Chi un simbolo universale non ce l'ha
  // resta a parole — inventarne uno lo renderebbe più oscuro, non più compatto.
  const comparison = operator ? (operator.symbol ?? operator.label) : SUMMARY_PLACEHOLDER;

  return { subject: `${field.label} ${comparison}`, value: value ?? SUMMARY_PLACEHOLDER };
}

/** La condizione in una stringa sola, per chi non ha bisogno di distinguerne i pezzi. */
function summarizeCondition(
  condition: SummarizableCondition,
  fields: CatalogField[],
  t: TFunction,
): string {
  const parts = summarizeConditionParts(condition, fields, t);

  return parts ? `${parts.subject} ${parts.value}` : SUMMARY_PLACEHOLDER;
}

/**
 * Il valore di una condizione, letto dall'oggetto persistito. La forma la detta la CARDINALITÀ dell'operatore
 * (uno / coppia / insieme / quantità relativa), la sorgente decide di cosa si stia parlando: con FIELD il termine destro
 * non è un letterale ma un altro campo, e va nominato con la sua etichetta.
 */
function formatConditionValue(
  condition: SummarizableCondition,
  field: CatalogField,
  operator: ConditionOperator,
  fields: CatalogField[],
  t: TFunction,
): string | null {
  const configuredValue = parseObject(condition.value);

  if (!configuredValue) return null;

  if (condition.valueSource === 'FIELD') {
    const rightField = fields.find((f) => f.fieldName === configuredValue.field);

    return rightField
      ? t('automation.summary.fieldRef', { field: rightField.label })
      : SUMMARY_PLACEHOLDER;
  }

  if (operator.cardinality === 'RANGE') {
    const from = formatScalar(field, configuredValue.from);
    const to = formatScalar(field, configuredValue.to);

    return from && to ? `${from} – ${to}` : null;
  }

  if (operator.cardinality === 'SET') {
    const values = Array.isArray(configuredValue.values)
      ? configuredValue.values.map((v) => formatScalar(field, v)).filter(Boolean)
      : [];

    return values.length > 0 ? values.join(', ') : null;
  }

  // RELATIVE: la quantità con la sua unità accordata («1 giorno», «30 giorni»), che l'operatore
  // a parole completa in una frase — «Data di partenza entro i prossimi 30 giorni».
  if (operator.cardinality === 'RELATIVE') {
    const amount = Number(configuredValue.value);

    if (configuredValue.value === undefined || configuredValue.value === null ||
      !Number.isFinite(amount))
      return null;

    const unit =
      typeof configuredValue.unit === 'string' && configuredValue.unit
        ? configuredValue.unit
        : DEFAULT_RELATIVE_UNIT;

    return t(`automation.summary.relativeUnits.${unit}`, { count: amount });
  }

  return formatScalar(field, configuredValue.value);
}

/**
 * Uno scalare come lo legge l'utente. Sugli ENUM il valore persistito è un codice (`SENT`), e
 * mostrarlo sarebbe gergo: si nomina con la label che il catalogo dichiara, la stessa della tendina.
 *
 * Esportato perché lo usa anche la tabella della simulazione, che deve rendere il valore LETTO dal
 * record mentre qui si rende quello CONFIGURATO: sono due valori diversi dello stesso campo, e
 * renderli con due funzioni diverse porterebbe a mostrare "Inviato" da una parte e `300` dall'altra
 * nella stessa schermata.
 *
 * Il parametro è tipizzato per **struttura** e non come `CatalogField`: a dichiarare un vocabolario
 * sono due descrittori diversi — il campo del SE e il parametro di un evento del QUANDO — e la
 * regola per leggerlo è la stessa. Chiedere il contenitore avrebbe voluto dire una seconda copia di
 * questa funzione, con l'esito prevedibile che una delle due prima o poi mostra il codice grezzo.
 */
export function formatScalar(
  field: { type: AutomationParamType; enumValues: CatalogEnumValue[] },
  value: unknown,
): string | null {
  if (value === undefined || value === null || value === '') return null;

  if (field.type === 'ENUM') {
    const enumValue = field.enumValues.find((e) => e.value === String(value));

    return enumValue?.label ?? String(value);
  }

  return String(value);
}

/**
 * Il valore di un parametro dell'evento: stessa grammatica del valore configurato, senza operatori
 * di mezzo.
 * <p>Riceve il parametro e non il solo valore, perché su un param `ENUM` il configurato è un codice
 * (`2`) e il riepilogo deve dire «Inviato», come già fa per il valore di una condizione. La regola
 * è quella di `formatScalar`, che a questo punto serve entrambi i descrittori.
 */
function formatParamValue(
  param: AutomationParam,
  paramValue: Record<string, unknown> | null,
): string | null {
  if (!paramValue) return null;

  const single = paramValue.value;

  if (single !== undefined && single !== null && single !== '')
    return formatScalar(param, single);

  // Parametro a intervallo: si mostra anche se aperto da un lato, perché è una configurazione
  // legittima (a differenza del BETWEEN di una condizione, che li pretende entrambi).
  const from = paramValue.from;
  const to = paramValue.to;

  if ((from ?? '') === '' && (to ?? '') === '') return null;

  return `${from ?? SUMMARY_PLACEHOLDER} – ${to ?? SUMMARY_PLACEHOLDER}`;
}

/**
 * Legge un oggetto JSON, che arrivi come stringa (il valore configurato persistito) o come oggetto
 * già parsato.
 * Un valore illeggibile non deve rompere il riepilogo: si comporta come un valore assente, e il
 * segnaposto dice che manca qualcosa. Chi deve protestare è il pannello Validazione, non questo box.
 */
function parseObject(value: unknown): Record<string, unknown> | null {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;

  if (typeof value !== 'string') return null;

  try {
    const parsed: unknown = JSON.parse(value);

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;

    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}
